import L from 'leaflet'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import type { FeatureCollection } from 'geojson'
import { BASEMAPS, buildLocationsLayer, buildOgdpLayer, buildSchoolLayer, buildSpillsLayer, buildTanksLayer, buildWellsLayer, createLegend, OGDPS } from './MapUtils'

;(globalThis as Record<string, unknown>).L = L

await import('leaflet-hash')
await import('leaflet-easybutton')
await import('leaflet.fullscreen')
await import('leaflet-textpath')

const markerIcon2x = new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href
const markerIcon = new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href
const markerShadow = new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow })

// Parse school code from URL: /maps/school/0060[/...]
function getSchoolCode(): string | null {
  const match = window.location.pathname.match(/\/maps\/school\/(\d+)/)
  return match ? match[1] : null
}

export async function initMap(): Promise<L.Map | null> {
  const schoolCode = getSchoolCode()

  if (!schoolCode) {
    console.log('No school code found in URL. Expected: /school/0060')
    return null
  }

  const base = import.meta.env.BASE_URL

  const results = await Promise.all([
    fetch(`${base}data/${schoolCode}.geojson`).then(r => { if (!r.ok) throw new Error(`School ${schoolCode} not found`); return r.json() }) as Promise<FeatureCollection>,
    fetch(`${base}data/${schoolCode}_locations.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}data/${schoolCode}_spills.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}data/${schoolCode}_tanks.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}data/${schoolCode}_wells.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
  ]).catch(err => { console.log(err); document.getElementById('error')!.textContent = err.message; return null })

  if (!results) return null

  const [schoolGeoJSON, locationsGeoJSON, spillsGeoJSON, tanksGeoJSON, wellsGeoJSON] = results

  if (!schoolGeoJSON) return null

  const ogdps = OGDPS.filter(o => o.schoolCodes.includes(schoolCode))
  const ogdpGeoJSONs = await Promise.all(
    ogdps.map(o => fetch(`${base}data/${o.file}`).then(r => r.json() as Promise<FeatureCollection>))
  )

  const { latitude: lat, longitude: lng, spills, wells, wells_active, wells_lp, wells_pa } = schoolGeoJSON.features[0].properties as Record<string, any>

  const map = L.map('map', { center:[lat, lng], zoom:15, zoomControl:true, zoomSnap:0.5 })

  const schoolLayer = buildSchoolLayer(schoolGeoJSON, map).addTo(map)
  schoolLayer.eachLayer(layer => (layer as L.Marker).openTooltip())
  // Added before the other feature layers so the OGDP shapes sit beneath them
  // in the shared SVG pane — otherwise they'd intercept hover/tooltips on the
  // smaller markers (locations, tanks, spills, wells) underneath.
  const ogdpLayers = ogdpGeoJSONs.map(geojson => buildOgdpLayer(geojson, map).addTo(map))
  const locationsLayer = buildLocationsLayer(locationsGeoJSON).addTo(map)
  const tanksLayer = buildTanksLayer(tanksGeoJSON).addTo(map)
  const spillsLayer = buildSpillsLayer(spillsGeoJSON).addTo(map)
  const wellsLayer = buildWellsLayer(wellsGeoJSON).addTo(map)

  createLegend([
    { label: `Locations 10+ Wells (${locationsGeoJSON.features.length.toLocaleString()})`, type: 'circle', color: '#000', fillColor: 'rgba(0,0,0,0.4)', weight: 1 },
    { label: `Active Wells (${(wells_active ?? 0).toLocaleString()})`, type: 'circle', color: '#c00', fillColor: 'rgba(204,0,0,0.4)', weight: 1 },
    { label: `Low Producing Wells (${(wells_lp ?? 0).toLocaleString()})`, type: 'circle', color: '#f90', fillColor: 'rgba(255,153,0,0.4)', weight: 1 },
    { label: `Plugged Wells (${(wells_pa ?? 0).toLocaleString()})`, type: 'circle', color: '#999', fillColor: 'rgba(153,153,153,0.4)', weight: 1 },
    { label: `Spills (${(spills ?? 0).toLocaleString()})`, type: 'polygon', color: '#ff1493', fillColor: 'rgba(255,105,180,0.2)', weight: 1, rotation: 45 },
    { label: `Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`, svg: `${base}tank.svg` },
    { label: '2,000 ft', type: 'circle', color: '#cc0000', fillColor: 'rgba(204,0,0,0.1)', weight: 2 },
    { label: '1 Mile', type: 'circle', color: '#ff7f00', fillColor: 'rgba(255,127,0,0.1)', weight: 2 },
    ...(ogdps.length ? [
      { label: 'OGDP Pad', type: 'polygon' as const, color: '#8d5a99', fillColor: 'rgba(141,90,153,0.6)', weight: 2, dashArray: '6,6' },
    ] : []),
  ], 'bottomleft').addTo(map)

  const logoControl = L.Control.extend({
    options: { position: 'bottomleft' },
    onAdd() {
      const container = L.DomUtil.create('div')
      L.DomEvent.disableClickPropagation(container)
      container.innerHTML = `<a href="https://ecocarto.com/" target="_blank"><img src="${base}logo.png" style="display:block;height:auto;width:180px;"></a>`
      return container
    },
  })
  new logoControl().addTo(map)

  L.easyButton('fa-solid fa-house', (_, map) => map.setView([lat, lng], 15)).addTo(map)
  new L.Hash(map)
  // @ts-expect-error — leaflet.fullscreen v5 type not yet updated
  new L.Control.FullScreen({ position: 'topleft', forceSeparateButton: false }).addTo(map)

  // @ts-expect-error — GeoSearchControl types are loose
  map.addControl(new GeoSearchControl({ provider: new OpenStreetMapProvider(), style: 'bar', showMarker: false, retainZoomLevel: false, autoClose: true, keepResult: true }))

  BASEMAPS['CartoDB Positron'].addTo(map)
  const ogdpOverlays = Object.fromEntries(
    ogdps.map((o, i) => [`${o.id[0].toUpperCase()}${o.id.slice(1)} OGDP`, ogdpLayers[i]])
  )
  L.control.layers(
    BASEMAPS,
    {
      ['School']: schoolLayer,
      [`Locations (${locationsGeoJSON.features.length.toLocaleString()})`]: locationsLayer,
      [`Spills (${(spills ?? 0).toLocaleString()})`]: spillsLayer,
      [`Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`]: tanksLayer,
      [`Wells (${(wells ?? 0).toLocaleString()})`]: wellsLayer,
      ...ogdpOverlays,
    },
    { position: 'topleft', collapsed: true }
  ).addTo(map)

  return map
}